import React, { useState } from 'react';
import {
    Box, Button, TextField, Typography, MenuItem, FormControl, InputLabel, Select,
} from '@mui/material';
import { REGIONS } from '../../../../utils/META/cat_101';
import {
    ORG_TYPE_OPTIONS, GOAL_OPTIONS, COUNT_BAND_OPTIONS, ROLLOUT_OPTIONS,
} from '../constants/cpa.constants';

// Free-text "Other" descriptions must be a real word, not a single stray char.
const MIN_OTHER_LEN = 2;

// The contact form now lives inside the floating chat panel (z-index 1301).
// MUI's Select menu defaults to z-index 1300, so it would render *behind* the
// panel. Lift every dropdown menu above the panel so the options are visible.
const SELECT_MENU_PROPS = { sx: { zIndex: 1402 } };

/** A single pill/chip button (matches the DQ Pro schedule picker styling). */
const Chip = ({ classes, label, selected, disabled, isOwn, onClick }) => (
    <Box
        className={[
            classes.chip,
            isOwn ? classes.chipOwn : '',
            selected ? classes.chipSelected : '',
            disabled ? classes.chipDisabled : '',
        ].join(' ')}
        onClick={disabled ? undefined : onClick}
    >
        {label}
    </Box>
);

export const CpaOrgTypeForm = ({ classes, disabled, onSubmit, errorCode }) => {
    const [orgType, setOrgType] = useState('');
    const [orgTypeOther, setOrgTypeOther] = useState('');
    const [orgTypeOtherTouched, setOrgTypeOtherTouched] = useState(false);

    const handleSubmit = () => {
        const label = ORG_TYPE_OPTIONS.find((o) => o.value === orgType)?.label || orgType;
        onSubmit(
            { org_type: orgType, org_type_other: orgType === 'other' ? orgTypeOther : '' },
            `I represent: ${label}${orgType === 'other' && orgTypeOther ? ` — ${orgTypeOther}` : ''}`
        );
    };

    return (
        <Box className={classes.chipFormWrap}>
            <Box className={classes.chipFormTitle}>I represent a(n):</Box>
            <Box className={classes.chipFormSubtitle}>Pick the option that best describes you.</Box>
            <Box className={classes.chipsColumn}>
                {ORG_TYPE_OPTIONS.map((opt) => (
                    <Chip
                        key={opt.value}
                        classes={classes}
                        label={opt.value === 'other' ? '✏️ Other' : opt.label}
                        isOwn={opt.value === 'other'}
                        selected={orgType === opt.value}
                        disabled={disabled}
                        onClick={() => setOrgType(opt.value)}
                    />
                ))}
            </Box>
            {orgType === 'other' && (
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Describe your organisation"
                    value={orgTypeOther}
                    onChange={(e) => setOrgTypeOther(e.target.value)}
                    onBlur={() => setOrgTypeOtherTouched(true)}
                    error={orgTypeOtherTouched && orgTypeOther.trim().length > 0 && orgTypeOther.trim().length < MIN_OTHER_LEN}
                    helperText={
                        orgTypeOtherTouched && orgTypeOther.trim().length > 0 && orgTypeOther.trim().length < MIN_OTHER_LEN
                            ? `Please enter at least ${MIN_OTHER_LEN} characters.`
                            : ''
                    }
                    disabled={disabled}
                    sx={{ mt: 1.5 }}
                />
            )}
            {errorCode && (
                <Typography variant="caption" className={classes.chipErrorText}>
                    Please complete this step.
                </Typography>
            )}
            <Box className={classes.chipContinueWrap}>
                <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    onClick={handleSubmit}
                    disabled={disabled || !orgType || (orgType === 'other' && orgTypeOther.trim().length < MIN_OTHER_LEN)}
                    sx={{
                        textTransform: 'none',
                        bgcolor: '#FF6900',
                        color: '#fff',
                        borderRadius: 999,
                        fontWeight: 600,
                        fontSize: 14,
                        py: 1.25,
                        '&:hover': { bgcolor: '#E65C00' },
                        '&.Mui-disabled': { bgcolor: '#f0c3a3', color: '#fff' },
                    }}
                >
                    Continue
                </Button>
            </Box>
        </Box>
    );
};

export const CpaGoalsForm = ({ classes, disabled, onSubmit, errorCode }) => {
    const [goals, setGoals] = useState([]);
    const [goalsOther, setGoalsOther] = useState('');
    const [goalsOtherTouched, setGoalsOtherTouched] = useState(false);

    const toggle = (value) => {
        setGoals((prev) =>
            prev.includes(value) ? prev.filter((g) => g !== value) : [...prev, value]
        );
    };

    const handleSubmit = () => {
        const labels = GOAL_OPTIONS.filter((o) => goals.includes(o.value)).map((o) => o.label);
        onSubmit(
            { goals, goals_other: goals.includes('other') ? goalsOther : '' },
            `Goals: ${labels.join('; ')}`
        );
    };

    return (
        <Box className={classes.chipFormWrap}>
            <Box className={classes.chipFormTitle}>What would you like to do?</Box>
            <Box className={classes.chipFormSubtitle}>Select all that apply.</Box>
            <Box className={classes.chipsColumn}>
                {GOAL_OPTIONS.map((opt) => (
                    <Chip
                        key={opt.value}
                        classes={classes}
                        label={opt.value === 'other' ? '✏️ Other' : opt.label}
                        isOwn={opt.value === 'other'}
                        selected={goals.includes(opt.value)}
                        disabled={disabled}
                        onClick={() => toggle(opt.value)}
                    />
                ))}
            </Box>
            {goals.includes('other') && (
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Describe your goal"
                    value={goalsOther}
                    onChange={(e) => setGoalsOther(e.target.value)}
                    onBlur={() => setGoalsOtherTouched(true)}
                    error={goalsOtherTouched && goalsOther.trim().length > 0 && goalsOther.trim().length < MIN_OTHER_LEN}
                    helperText={
                        goalsOtherTouched && goalsOther.trim().length > 0 && goalsOther.trim().length < MIN_OTHER_LEN
                            ? `Please enter at least ${MIN_OTHER_LEN} characters.`
                            : ''
                    }
                    disabled={disabled}
                    sx={{ mt: 1.5 }}
                />
            )}
            {errorCode && (
                <Typography variant="caption" className={classes.chipErrorText}>
                    Please select at least one goal.
                </Typography>
            )}
            <Box className={classes.chipContinueWrap}>
                <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    onClick={handleSubmit}
                    disabled={
                        disabled ||
                        goals.length === 0 ||
                        (goals.includes('other') && goalsOther.trim().length < MIN_OTHER_LEN)
                    }
                    sx={{
                        textTransform: 'none',
                        bgcolor: '#FF6900',
                        color: '#fff',
                        borderRadius: 999,
                        fontWeight: 600,
                        fontSize: 14,
                        py: 1.25,
                        '&:hover': { bgcolor: '#E65C00' },
                        '&.Mui-disabled': { bgcolor: '#f0c3a3', color: '#fff' },
                    }}
                >
                    Continue
                </Button>
            </Box>
        </Box>
    );
};

// PDF-faithful quote step: the mockup shows a single affirmative action that
// echoes as a user bubble. There is no "No, thanks" button in the PDF, so the
// primary CTA dominates and declining is demoted to a quiet text link (kept so
// the decline → application_complete flow still works).
export const CpaQuotePrompt = ({ disabled, onYes, onNo }) => (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
        <Button
            variant="contained"
            disabled={disabled}
            onClick={() => onYes(true, "Yes, I'd like a quote")}
            sx={{
                textTransform: 'none',
                bgcolor: '#0097B2',
                borderRadius: '20px',
                px: 2.5,
                '&:hover': { bgcolor: '#007A91' },
            }}
        >
            Yes, I&apos;d like a quote
        </Button>
        <Button
            variant="text"
            size="small"
            disabled={disabled}
            onClick={() => onNo(false, 'No thanks, maybe later')}
            sx={{
                textTransform: 'none',
                color: '#9aa0a6',
                fontSize: 13,
                p: 0,
                minWidth: 0,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
            }}
        >
            No thanks, maybe later
        </Button>
    </Box>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BandSelect = ({ label, value, onChange, disabled }) => (
    <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
        <InputLabel>{label}</InputLabel>
        <Select value={value} label={label} onChange={onChange} disabled={disabled} MenuProps={SELECT_MENU_PROPS}>
            {COUNT_BAND_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'none'} value={o.value} disabled={o.value === ''}>
                    {o.label}
                </MenuItem>
            ))}
        </Select>
    </FormControl>
);

export const CpaContactForm = ({ disabled, onSubmit, errorCode }) => {
    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        title: '',
        organization_name: '',
        address: '',
        city: '',
        postal_code: '',
        email: '',
        country: '',
        num_children: '',
        num_parents: '',
        num_teachers: '',
        num_schools: '',
        num_companies: '',
        rollout_period: '',
        translations_needed: '',
        translations_language: '',
        phone_number: '',
        message: '',
    });
    const [touchedEmail, setTouchedEmail] = useState(false);

    const setField = (key) => (event) => {
        const value = event && event.target !== undefined ? event.target.value : event;
        setForm((f) => ({ ...f, [key]: value }));
    };

    const emailInvalid = touchedEmail && form.email.trim() !== '' && !EMAIL_RE.test(form.email.trim());

    const requiredFilled =
        form.first_name.trim() &&
        form.last_name.trim() &&
        form.organization_name.trim() &&
        EMAIL_RE.test(form.email.trim()) &&
        form.country;

    const handleSubmit = () => {
        onSubmit(form, `Quote request — ${form.organization_name}`);
    };

    return (
        <Box sx={{ mt: 1, p: 2, bgcolor: 'var(--brand-surface, #fff)', borderRadius: 2, border: '1px solid var(--brand-border, #f0e3d6)' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                Ok, please fill out the following form:
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                    required fullWidth size="small" label="First Name"
                    value={form.first_name} onChange={setField('first_name')} disabled={disabled}
                />
                <TextField
                    required fullWidth size="small" label="Last Name"
                    value={form.last_name} onChange={setField('last_name')} disabled={disabled}
                />
            </Box>

            <TextField
                fullWidth size="small" label="Your Title"
                value={form.title} onChange={setField('title')} disabled={disabled}
                sx={{ mt: 1.5 }}
            />
            <TextField
                required fullWidth size="small" label="Organization Name"
                value={form.organization_name} onChange={setField('organization_name')} disabled={disabled}
                sx={{ mt: 1.5 }}
            />
            <TextField
                fullWidth size="small" label="Office Address"
                value={form.address} onChange={setField('address')} disabled={disabled}
                sx={{ mt: 1.5 }}
            />
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
                <TextField
                    fullWidth size="small" label="City"
                    value={form.city} onChange={setField('city')} disabled={disabled}
                />
                <TextField
                    fullWidth size="small" label="Postal Code"
                    value={form.postal_code} onChange={setField('postal_code')} disabled={disabled}
                />
            </Box>
            <TextField
                required fullWidth size="small" type="email" label="Your Email"
                value={form.email} onChange={setField('email')}
                onBlur={() => setTouchedEmail(true)}
                error={emailInvalid}
                helperText={emailInvalid ? 'Enter a valid email address.' : ''}
                disabled={disabled}
                sx={{ mt: 1.5 }}
            />

            <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                <InputLabel required>Country you intend to assess</InputLabel>
                <Select required value={form.country} label="Country you intend to assess" onChange={setField('country')} disabled={disabled} MenuProps={SELECT_MENU_PROPS}>
                    {REGIONS.map((r) => (
                        <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Typography variant="caption" sx={{ display: 'block', mt: 2, mb: 0.5, fontWeight: 700, color: 'var(--brand-text-muted, #555)' }}>
                How many people do you intend to assess, and over what roll-out period?
            </Typography>
            <BandSelect label="# of children you intend to assess" value={form.num_children} onChange={setField('num_children')} disabled={disabled} />
            <BandSelect label="# of parents you intend to assess" value={form.num_parents} onChange={setField('num_parents')} disabled={disabled} />
            <BandSelect label="# of teachers you intend to assess" value={form.num_teachers} onChange={setField('num_teachers')} disabled={disabled} />
            <BandSelect label="# of schools you intend to assess" value={form.num_schools} onChange={setField('num_schools')} disabled={disabled} />
            <BandSelect label="# of companies you intend to assess" value={form.num_companies} onChange={setField('num_companies')} disabled={disabled} />

            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <InputLabel>When do you plan to roll this out?</InputLabel>
                <Select
                    value={form.rollout_period}
                    label="When do you plan to roll this out?"
                    onChange={setField('rollout_period')}
                    disabled={disabled}
                    MenuProps={SELECT_MENU_PROPS}
                >
                    {ROLLOUT_OPTIONS.map((o) => (
                        <MenuItem key={o.value || 'none'} value={o.value} disabled={o.value === ''}>
                            {o.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                <InputLabel>Do you need translations?</InputLabel>
                <Select
                    value={form.translations_needed}
                    label="Do you need translations?"
                    onChange={setField('translations_needed')}
                    disabled={disabled}
                    MenuProps={SELECT_MENU_PROPS}
                >
                    <MenuItem value="">No</MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                </Select>
            </FormControl>
            {form.translations_needed === 'Yes' && (
                <TextField
                    fullWidth size="small" label="If so, which language(s)?"
                    value={form.translations_language} onChange={setField('translations_language')} disabled={disabled}
                    sx={{ mt: 1.5 }}
                />
            )}

            <TextField
                fullWidth size="small" label="Phone Number"
                value={form.phone_number} onChange={setField('phone_number')} disabled={disabled}
                sx={{ mt: 1.5 }}
            />
            <TextField
                fullWidth
                size="small"
                margin="none"
                multiline
                rows={2}
                label="Anything else you'd like us to know?"
                value={form.message}
                onChange={setField('message')}
                disabled={disabled}
                sx={{ mt: 1.5 }}
            />

            {errorCode && (
                <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                    {errorCode === 'INVALID_EMAIL'
                        ? 'Please enter a valid email address.'
                        : errorCode === 'EMAIL_ALREADY_EXISTS'
                            ? 'An account with this email already exists. Please use a different email.'
                            : 'Please fill all required fields.'}
                </Typography>
            )}
            <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={disabled || !requiredFilled}
                sx={{
                    mt: 2,
                    textTransform: 'none',
                    bgcolor: '#FF6900',
                    '&:hover': { bgcolor: '#E65C00' },
                }}
            >
                Submit
            </Button>
        </Box>
    );
};
